import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useSEO } from "@/lib/seo";
import { useAuth } from "@/contexts/AuthContext";

const MIN_NOTES = 5;

export default function CreatePortfolio() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [asking, setAsking] = useState("");
  const [busy, setBusy] = useState(false);
  const [feeQuote, setFeeQuote] = useState(null);

  useSEO({
    title: "Create Portfolio Listing — FinanceLocal",
    description: "Bundle 5 or more vehicle notes into a portfolio and list them on the FinanceLocal marketplace.",
    canonical: "https://myfinancelocal.com/portfolios/new",
    noIndex: true,
  });

  useEffect(() => {
    api.get("/notes", { params: { sort: "yield_desc" } })
      .then((r) => setAvailable(r.data.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const p = Number(asking) || 0;
    if (p <= 0) { setFeeQuote(null); return; }
    api.get("/marketplace/fees/quote", { params: { price: p } }).then((r) => setFeeQuote(r.data)).catch(() => {});
  }, [asking]);

  const toggle = (nid) => {
    const s = new Set(selected);
    if (s.has(nid)) s.delete(nid); else s.add(nid);
    setSelected(s);
  };

  const submit = async () => {
    if (selected.size < MIN_NOTES) { toast.error(`Select at least ${MIN_NOTES} notes`); return; }
    if (!title.trim()) { toast.error("Title is required"); return; }
    const price = Number(asking);
    if (!price || price <= 0) { toast.error("Asking price must be > 0"); return; }
    setBusy(true);
    try {
      const r = await api.post("/marketplace/portfolios", {
        title, description,
        asking_price: price,
        note_ids: Array.from(selected),
      });
      toast.success("Portfolio listed");
      navigate(`/portfolios/${r.data.portfolio_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create portfolio");
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <div data-testid="page-create-portfolio" className="mx-auto max-w-4xl px-4 py-10">
      <div className="border-b border-border pb-6 mb-8">
        <div className="font-overline text-accent">Marketplace</div>
        <h1 className="font-display text-4xl mt-2">Create Portfolio Listing</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Bundle {MIN_NOTES}+ notes into a single portfolio. Buyers see aggregate stats publicly and must request access to view individual loan documents.
        </p>
      </div>

      <div className="grid gap-4 mb-8">
        <div><Label className="font-overline mb-1 block text-xs">Portfolio Title</Label>
          <Input data-testid="portfolio-title-input" className="rounded-none" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Texas Sub-Prime Portfolio #1" />
        </div>
        <div><Label className="font-overline mb-1 block text-xs">Description</Label>
          <Textarea data-testid="portfolio-description-input" rows={3} className="rounded-none" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div><Label className="font-overline mb-1 block text-xs">Asking Price (USD)</Label>
          <Input data-testid="portfolio-price-input" type="number" className="rounded-none font-mono-data" value={asking} onChange={(e) => setAsking(e.target.value)} />
        </div>
        {feeQuote && (
          <div className="border border-border p-4 text-sm space-y-1" data-testid="portfolio-fee-preview">
            <div className="font-overline text-muted-foreground mb-1">Fees at this price</div>
            <div className="flex justify-between"><span>Buyer fee</span><span className="font-mono-data">{fmtMoney(feeQuote.buyer_fee)}</span></div>
            <div className="flex justify-between"><span>Seller fee</span><span className="font-mono-data">{fmtMoney(feeQuote.seller_fee)}</span></div>
            <div className="flex justify-between font-semibold border-t border-border pt-1"><span>Combined</span><span className="font-mono-data">{fmtMoney(feeQuote.combined_fee)}</span></div>
            {feeQuote.minimum_applied && <div className="text-[11px] text-amber-700 dark:text-amber-300">Minimum $500 combined fee applied.</div>}
          </div>
        )}
      </div>

      <div className="border-t border-border pt-6">
        <div className="flex items-center justify-between mb-3">
          <div className="font-overline text-muted-foreground">Select Notes ({selected.size} / min {MIN_NOTES})</div>
          <div className="text-xs text-muted-foreground">{available.length} available</div>
        </div>
        <div className="border border-border divide-y divide-border max-h-[500px] overflow-y-auto" data-testid="portfolio-notes-picker">
          {available.map((n) => (
            <label key={n.note_id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/40 cursor-pointer">
              <Checkbox data-testid={`pick-note-${n.note_id}`} className="rounded-none"
                checked={selected.has(n.note_id)}
                onCheckedChange={() => toggle(n.note_id)} />
              <div className="flex-1 grid grid-cols-4 gap-2 text-sm">
                <span>{n.vehicle_year} {n.vehicle_make} {n.vehicle_model}</span>
                <span className="text-muted-foreground">Grade {n.grade} · {n.state}</span>
                <span className="font-mono-data text-primary">{fmtPct(n.estimated_yield)}</span>
                <span className="font-mono-data text-right">{fmtMoney(n.remaining_balance)}</span>
              </div>
            </label>
          ))}
        </div>
      </div>

      <Button data-testid="portfolio-submit" onClick={submit} disabled={busy || selected.size < MIN_NOTES}
        className="rounded-none uppercase tracking-wider w-full h-12 mt-8">
        {busy ? "Creating…" : `Create Portfolio Listing (${selected.size} notes)`}
      </Button>
    </div>
  );
}
